import { AvailabilityItem, AvailabilityItemError } from 'api/Serializers/Cart';
import Button from 'components/button';
import Callout from 'components/callout';
import Controls from 'components/controls';
import Modal from 'components/modal';
import { DATE_FMT } from 'config';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { ErrorFilledIcon, SuccessFilledIcon } from 'icons';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  getCart,
  getCartAppointmentProduct,
  getCartBookingType,
  getCartError,
  getCartMultiAvailabilityOrigin,
  getCartSingleAvailability,
  getCartStatus,
} from 'state/selectors';
import {
  FINISHED_STATUSES,
  getApptProdBookingPathAndParams,
  setMultiAvailabilityList,
  setMultiAvailabilityOrigin,
} from 'state/slice/cart';
import { SHARED_ROUTES } from 'utils/routing';
import { getDatetimeStr, toLocalTime } from '../utils';

interface CheckoutResultChildProps {
  onCartReset(): void;
  onClose(): void;
}

export const AvailabilityItemErrorMessage: Record<
  AvailabilityItemError,
  string
> = {
  AVAILABILITY_NOT_BOOKABLE: 'Time is no longer bookable',
  PAYMENT_METHOD_FAILED: 'Your payment method failed',
  MAX_DAILY_BOOKINGS: 'Max daily bookings reached',
  MAX_TOTAL_BOOKINGS: 'Max future bookings reached',
  OTHER: 'An unexpected error occurred',
};

const CheckoutResultControls = ({
  numSucceeded,
  numFailed,
  onClose,
  onCartReset,
}: {
  numSucceeded: number;
  numFailed: number;
  onClose(): void;
  onCartReset(): void;
}) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const appointmentProduct = useSelector(getCartAppointmentProduct);
  const multiAvailabilityOrigin = useSelector(getCartMultiAvailabilityOrigin);
  const bookingType = useSelector(getCartBookingType);
  const cart = useSelector(getCart);
  const cartAvailability = cart.availability as AvailabilityItem[];

  const handleBookMoreClick = () => {
    rudderanalytics.track('Post Purchase Book More clicked');
    onCartReset();
  };

  const handleMessageInstructorClick = () => {
    rudderanalytics.track('Post Purchase Message Instructor clicked');
    onCartReset();
  };

  const handleBackPartialFailureMulti = () => {
    const transformedAvailabilityList = cartAvailability
      .filter((item) => item.status === 'ERROR')
      .map((item) => ({
        id: item.availability,
        start: item.start,
        end: item.end,
        isBookable: item.error !== 'AVAILABILITY_NOT_BOOKABLE',
      }));
    onCartReset();
    dispatch(setMultiAvailabilityOrigin(multiAvailabilityOrigin));
    dispatch(setMultiAvailabilityList(transformedAvailabilityList));
    history.push(
      getApptProdBookingPathAndParams(location, appointmentProduct, bookingType)
    );
  };

  return (
    <Controls className="!pb-0">
      {numSucceeded === 0 ? (
        <Button variant="contained" onClick={onClose}>
          Back
        </Button>
      ) : (
        <>
          <Button
            variant="contained"
            onClick={onCartReset}
            to={SHARED_ROUTES.SCHEDULE.ROOT}
          >
            Exit
          </Button>
          <Button
            variant="contained"
            onClick={handleMessageInstructorClick}
            to={SHARED_ROUTES.MESSAGES.nav(cart.messageId)}
          >
            Message instructor
          </Button>
          {numFailed === 0 ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleBookMoreClick}
            >
              Book more
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleBackPartialFailureMulti}
            >
              Back
            </Button>
          )}
        </>
      )}
    </Controls>
  );
};

const CheckoutResultMulti = ({
  onCartReset,
  onClose,
}: CheckoutResultChildProps) => {
  const cart = useSelector(getCart);
  const cartStatus = useSelector(getCartStatus);
  const error = useSelector(getCartError);
  const cartAvailability = cart.availability as AvailabilityItem[];
  const appointmentProduct = useSelector(getCartAppointmentProduct);
  const bookedTimes = cartAvailability.filter((a) => a.status === 'BOOKED');
  const failedTimes = cartAvailability.filter((a) => a.status === 'ERROR');
  const bookedCount = bookedTimes.length;
  const failedCount = failedTimes.length;
  const listClasses = 'mb-4 leading-8';

  if (cartStatus === 'ERROR' && failedCount === 0 && bookedCount === 0) {
    return (
      <>
        <div>
          <Callout
            className="mb-2"
            type="error"
            title="Your booking is not complete"
          >
            {error.message}
          </Callout>
        </div>
        <CheckoutResultControls
          numSucceeded={bookedCount}
          numFailed={failedCount}
          onCartReset={onCartReset}
          onClose={onClose}
        />
      </>
    );
  }

  return (
    <div>
      <p>
        {failedCount === 0
          ? `Your ${bookedCount} ${'time'.pluralize(bookedCount)} ${
              bookedCount > 1 ? 'have' : 'has'
            } been successfully booked.`
          : bookedCount === 0
          ? `Your ${failedCount} ${'booking'.pluralize(failedCount)} failed.`
          : `${bookedCount} of your bookings succeeded and ${failedCount} failed.`}
      </p>
      {bookedCount > 0 && (
        <>
          {failedCount > 0 && <h4 className="text-md">Succeeded</h4>}
          <ul className={listClasses}>
            {bookedTimes.map((avail) => (
              <div className="flex items-center gap-2" key={avail.start}>
                <SuccessFilledIcon width={20} className="text-green-500" />
                <li key={avail.start}>
                  {getDatetimeStr(appointmentProduct, avail)}
                </li>
              </div>
            ))}
          </ul>
        </>
      )}
      {failedCount > 0 && (
        <>
          {bookedCount > 0 && <h4 className="text-md">Failed</h4>}
          <ul className={listClasses}>
            {failedTimes.map((avail) => (
              <div className="flex items-center gap-2" key={avail.start}>
                <ErrorFilledIcon width={20} className="text-red-500" />
                <li key={avail.start}>
                  {getDatetimeStr(appointmentProduct, avail)}
                  {avail.error !== 'OTHER'
                    ? `  \u2013  ${AvailabilityItemErrorMessage[avail.error]}`
                    : ''}
                </li>
              </div>
            ))}
          </ul>
        </>
      )}
      <CheckoutResultControls
        numSucceeded={bookedCount}
        numFailed={failedCount}
        onCartReset={onCartReset}
        onClose={onClose}
      />
    </div>
  );
};

const CheckoutResultSingle = ({
  onCartReset,
  onClose,
}: CheckoutResultChildProps) => {
  const appointmentProduct = useSelector(getCartAppointmentProduct);
  const singleAvailability = useSelector(getCartSingleAvailability);
  const cartStatus = useSelector(getCartStatus);
  const error = useSelector(getCartError);

  return (
    <>
      {cartStatus === 'ERROR' ? (
        <div>
          <Callout
            className="mb-2"
            type="error"
            title="Your booking is not complete"
          >
            {error.message}
          </Callout>
        </div>
      ) : (
        <div>
          <p>
            You have booked a 1 hour{' '}
            {appointmentProduct.activity.name.toLowerCase()} with{' '}
            <strong>{appointmentProduct.instructor.displayName}</strong> at{' '}
            <strong>{appointmentProduct.facility.displayName}</strong> for{' '}
            <strong>
              {toLocalTime(
                singleAvailability.start,
                appointmentProduct.timezone
              ).format(DATE_FMT.MONTH_D_YEAR_TIME_A)}
            </strong>
            . Would you like to book more?
          </p>
        </div>
      )}
      <CheckoutResultControls
        numSucceeded={cartStatus !== 'ERROR' ? 1 : 0}
        numFailed={cartStatus !== 'ERROR' ? 0 : 1}
        onCartReset={onCartReset}
        onClose={onClose}
      />
    </>
  );
};

const CheckoutResultModal = ({ onCartReset }) => {
  const [showModal, setShowModal] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const bookingType = useSelector(getCartBookingType);
  const cartStatus = useSelector(getCartStatus);
  const error = useSelector(getCartError);

  useEffect(() => {
    setShowModal(FINISHED_STATUSES.includes(cartStatus));
    if (cartStatus === 'ERROR') {
      enqueueSnackbar({
        message: error.message,
        variant: 'error',
      });
    }
  }, [cartStatus]);

  const handleClose = () => setShowModal(false);

  const title =
    cartStatus === 'COMPLETE'
      ? 'Thanks for booking!'
      : cartStatus === 'ERROR'
      ? 'Booking not complete'
      : null;

  return (
    <Modal
      name={`Checkout — ${title}`}
      open={showModal}
      title={title}
      maxWidth="sm"
      fullWidth
    >
      {!FINISHED_STATUSES.includes(cartStatus) ? null : bookingType ===
        'multi' ? (
        <CheckoutResultMulti onClose={handleClose} onCartReset={onCartReset} />
      ) : (
        <CheckoutResultSingle onClose={handleClose} onCartReset={onCartReset} />
      )}
    </Modal>
  );
};

export default CheckoutResultModal;
